import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import RouteIcon from "@mui/icons-material/Route";
import RuleIcon from "@mui/icons-material/Rule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { getFundRules } from "../../../Utils/fundRules";

const C = {
  bg: "#070a12",
  surface: "#101521",
  surface2: "#151c2b",
  border: "rgba(255,255,255,0.10)",
  borderStrong: "rgba(255,255,255,0.18)",
  white: "#ffffff",
  text: "rgba(255,255,255,0.84)",
  muted: "rgba(255,255,255,0.62)",
  faint: "rgba(255,255,255,0.42)",
  accent: "#2af598",
  accentDim: "rgba(42,245,152,0.12)",
  accentBorder: "rgba(42,245,152,0.32)",
  warn: "#f5c518",
  warnDim: "rgba(245,197,24,0.10)",
  warnBorder: "rgba(245,197,24,0.34)",
  danger: "#ff5b70",
  dangerDim: "rgba(255,91,112,0.10)",
  dangerBorder: "rgba(255,91,112,0.34)",
  info: "#42a5f5",
  infoDim: "rgba(66,165,245,0.10)",
  infoBorder: "rgba(66,165,245,0.34)",
};

const FP = {
  app:  { c: "#42a5f5", bg: "rgba(66,165,245,0.08)",  b: "rgba(66,165,245,0.30)",  tag: "APP"    },
  cf:   { c: "#2af598", bg: "rgba(42,245,152,0.08)",  b: "rgba(42,245,152,0.30)",  tag: "CF"     },
  dec:  { c: "#f5c518", bg: "rgba(245,197,24,0.08)",  b: "rgba(245,197,24,0.36)",  tag: "◆"      },
  man:  { c: "#ff5b70", bg: "rgba(255,91,112,0.10)",  b: "rgba(255,91,112,0.30)",  tag: "MANUAL" },
  wait: { c: "#ff9800", bg: "rgba(255,152,0,0.10)",   b: "rgba(255,152,0,0.30)",   tag: "AGUARDA"},
  paid: { c: "#2af598", bg: "rgba(42,245,152,0.16)",  b: "rgba(42,245,152,0.55)",  tag: "PAID"   },
  trg:  { c: "#ce93d8", bg: "rgba(206,147,216,0.09)", b: "rgba(206,147,216,0.30)", tag: "⚡"      },
};

const FLOW_W = 1080;
const LANE = { no: "36%", bridge: "52px", main: "1fr" };

const FTag = ({ type, label }) => {
  const p = FP[type];
  return (
    <Box sx={{ px: 1, py: 0.2, bgcolor: `${p.c}20`, border: `1px solid ${p.b}`, borderRadius: 1, fontSize: 10, fontWeight: 900, color: p.c, fontFamily: "monospace", whiteSpace: "nowrap", flexShrink: 0 }}>
      {label || p.tag}
    </Box>
  );
};

const FlowCanvas = ({ children }) => (
  <Box sx={{ maxWidth: FLOW_W, mx: "auto", width: "100%", px: { xs: 0.5, md: 1 } }}>
    {children}
  </Box>
);

// Eixo principal — coluna direita; esquerda fica vazia (só gates usam a esquerda)
const MainLane = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: `${LANE.no} ${LANE.bridge} ${LANE.main}` },
      columnGap: 0,
    }}
  >
    <Box sx={{ display: { xs: "none", md: "block" } }} />
    <Box sx={{ display: { xs: "none", md: "block" } }} />
    <Box>{children}</Box>
  </Box>
);

const FArrow = ({ color = "rgba(42,245,152,0.55)" }) => (
  <Box sx={{ width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: `8px solid ${color}` }} />
);

const FNode = ({ type, title, rows = [], mono = false }) => {
  const p = FP[type];
  return (
    <Box sx={{ border: `1px solid ${p.b}`, borderLeft: `4px solid ${p.c}`, borderRadius: "4px 12px 12px 4px", bgcolor: p.bg, p: { xs: 1.75, md: 2 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: rows.length ? 0.75 : 0 }}>
        <FTag type={type} />
        <Typography sx={{ color: C.white, fontWeight: 800, fontSize: 15, lineHeight: 1.35 }}>{title}</Typography>
      </Stack>
      {rows.map((r, i) => (
        <Typography key={i} sx={{ color: C.muted, fontSize: 13, lineHeight: 1.55, fontFamily: mono ? "monospace" : undefined }}>{r}</Typography>
      ))}
    </Box>
  );
};

const FConn = ({ label }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 0.75 }}>
    <Box sx={{ width: 2, height: 22, bgcolor: "rgba(42,245,152,0.40)" }} />
    {label && (
      <Box sx={{ bgcolor: C.surface2, border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.25, py: 0.35, my: 0.5 }}>
        <Typography sx={{ color: C.faint, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.14em" }}>{label}</Typography>
      </Box>
    )}
    <Box sx={{ width: 2, height: 22, bgcolor: "rgba(42,245,152,0.40)" }} />
    <FArrow />
  </Box>
);

const FNoBox = ({ type, title, rows }) => {
  const p = FP[type];
  return (
    <Box sx={{ border: `1px solid ${p.b}`, borderLeft: `3px solid ${p.c}`, borderRadius: 2, bgcolor: p.bg, p: 1.5, height: "100%" }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
        <FTag type={type} />
        <Typography sx={{ color: C.white, fontWeight: 800, fontSize: 13, lineHeight: 1.3 }}>{title}</Typography>
      </Stack>
      {rows.map((r) => (
        <Typography key={r} sx={{ color: C.muted, fontSize: 12, lineHeight: 1.45 }}>{r}</Typography>
      ))}
    </Box>
  );
};

// Gate profissional: NÃO à esquerda · decisão à direita · SIM desce só na coluna principal
const FGate = ({ question, noType = "man", noTitle, noRows = [] }) => {
  const noP = FP[noType];
  return (
    <Box sx={{ py: 1 }}>
      {/* Desktop: 3 colunas — NÃO | ponte | Decisão+SIM */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: `${LANE.no} ${LANE.bridge} ${LANE.main}`,
          gridTemplateRows: "auto auto",
          columnGap: 0,
          alignItems: "stretch",
        }}
      >
        {/* Col 1 — saída NÃO, alinhada verticalmente com a decisão */}
        <Box sx={{ gridColumn: 1, gridRow: 1, display: "flex", alignItems: "center", pr: 1 }}>
          <FNoBox type={noType} title={noTitle} rows={noRows} />
        </Box>

        {/* Col 2 — ponte horizontal NÃO → decisão */}
        <Box sx={{ gridColumn: 2, gridRow: 1, position: "relative", minHeight: 48 }}>
          <Box sx={{ position: "absolute", top: "50%", left: 0, right: 0, height: 2, bgcolor: noP.c, opacity: 0.55, transform: "translateY(-50%)" }} />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              px: 0.75,
              py: 0.2,
              bgcolor: `${noP.c}18`,
              border: `1px solid ${noP.b}`,
              borderRadius: 1,
              fontSize: 10,
              fontWeight: 900,
              color: noP.c,
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              zIndex: 1,
            }}
          >
            NÃO
          </Box>
          <Box sx={{ position: "absolute", top: "50%", right: -1, width: 6, height: 6, borderRadius: "50%", bgcolor: noP.c, opacity: 0.7, transform: "translateY(-50%)" }} />
        </Box>

        {/* Col 3 row 1 — decisão */}
        <Box sx={{ gridColumn: 3, gridRow: 1, pl: 0.5 }}>
          <Box sx={{ border: `2px dashed ${FP.dec.b}`, borderRadius: 3, bgcolor: FP.dec.bg, p: 2, position: "relative" }}>
            <Box sx={{ position: "absolute", left: -5, top: "50%", width: 6, height: 6, borderRadius: "50%", bgcolor: FP.dec.c, transform: "translateY(-50%)" }} />
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Typography sx={{ color: FP.dec.c, fontSize: 20, lineHeight: 1, flexShrink: 0 }}>◆</Typography>
              <Typography sx={{ color: C.white, fontWeight: 800, fontSize: 14.5, lineHeight: 1.45 }}>{question}</Typography>
            </Stack>
          </Box>
        </Box>

        {/* Col 3 row 2 — SIM (altura fixa, sem stretch) */}
        <Box sx={{ gridColumn: 3, gridRow: 2, display: "flex", flexDirection: "column", alignItems: "center", pt: 0.5, pb: 0.25 }}>
          <Box sx={{ width: 2, height: 20, bgcolor: "rgba(42,245,152,0.42)" }} />
          <Box sx={{ px: 1, py: 0.25, bgcolor: `${FP.cf.c}18`, border: `1px solid ${FP.cf.b}`, borderRadius: 1, fontSize: 10, fontWeight: 900, color: FP.cf.c, fontFamily: "monospace", my: 0.4 }}>SIM</Box>
          <Box sx={{ width: 2, height: 20, bgcolor: "rgba(42,245,152,0.42)" }} />
          <FArrow />
        </Box>
      </Box>

      {/* Mobile: decisão → NÃO lateral compacto → SIM */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Box sx={{ border: `2px dashed ${FP.dec.b}`, borderRadius: 3, bgcolor: FP.dec.bg, p: 1.75, mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Typography sx={{ color: FP.dec.c, fontSize: 18 }}>◆</Typography>
            <Typography sx={{ color: C.white, fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>{question}</Typography>
          </Stack>
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "stretch" }}>
          <Box sx={{ width: 3, bgcolor: noP.c, opacity: 0.5, borderRadius: 1, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: noP.c, fontSize: 10, fontWeight: 900, fontFamily: "monospace", mb: 0.5 }}>NÃO →</Typography>
            <FNoBox type={noType} title={noTitle} rows={noRows} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={{ width: 2, height: 16, bgcolor: "rgba(42,245,152,0.42)" }} />
          <Box sx={{ px: 1, py: 0.25, bgcolor: `${FP.cf.c}18`, border: `1px solid ${FP.cf.b}`, borderRadius: 1, fontSize: 10, fontWeight: 900, color: FP.cf.c, fontFamily: "monospace", my: 0.4 }}>SIM</Box>
          <Box sx={{ width: 2, height: 16, bgcolor: "rgba(42,245,152,0.42)" }} />
          <FArrow />
        </Box>
      </Box>
    </Box>
  );
};

const FlowchartDialog = ({ open, onClose, ap }) => {
  const fmtMin = (v) => (v != null ? `${v}min${Number(v) >= 60 ? ` (~${Math.round(Number(v) / 60)}h)` : ""}` : "—");
  const fmtMoney = (v) => (v != null && Number(v) > 0 ? `$${Number(v).toLocaleString("en-US")}` : "—");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      scroll="paper"
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.84)", backdropFilter: "blur(10px)" } }}
      PaperProps={{
        sx: {
          bgcolor: "#07101c",
          color: C.white,
          border: `2px solid ${C.accentBorder}`,
          borderRadius: 4,
          minHeight: "90vh",
          boxShadow: "0 40px 140px rgba(0,0,0,0.80), 0 0 0 1px rgba(42,245,152,0.06)",
          background: "radial-gradient(circle at 18% 0%, rgba(42,245,152,0.16) 0%, transparent 30%), radial-gradient(circle at 92% 8%, rgba(66,165,245,0.13) 0%, transparent 26%), linear-gradient(160deg, #08101c 0%, #050810 100%)",
        },
      }}
    >
      <DialogTitle sx={{ p: { xs: 2.5, md: 3 }, borderBottom: `1px solid ${C.borderStrong}`, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ color: C.accent, fontSize: 11, fontWeight: 900, letterSpacing: "0.20em", textTransform: "uppercase", mb: 0.5 }}>
              Fluxograma completo — Auto-Pay Fase 1
            </Typography>
            <Typography sx={{ color: C.white, fontSize: { xs: 22, md: 32 }, fontWeight: 950, lineHeight: 1.08 }}>
              Do pedido no app ao pagamento on-chain
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 1.25, gap: 0.75 }}>
              {[["APP / Cliente", FP.app.c], ["CF Automático", FP.cf.c], ["Decisão / Gate", FP.dec.c], ["MANUAL", FP.man.c], ["AGUARDA", FP.wait.c]].map(([label, color]) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: 0.5, flexShrink: 0 }} />
                  <Typography sx={{ color: C.muted, fontSize: 12 }}>{label}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: C.white, border: `1px solid ${C.borderStrong}`, bgcolor: "rgba(255,255,255,0.04)", "&:hover": { bgcolor: "rgba(255,255,255,0.08)" }, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <FlowCanvas>

          <MainLane><FNode type="app" title="Cliente abre app e solicita saque" rows={["request_status: 0 (Processing)", "OTP ainda não verificado — saldo não movimentado"]} /></MainLane>
          <MainLane><FConn label="OTP verificado" /></MainLane>
          <MainLane><FNode type="app" title="App verifica OTP e deduz saldo imediatamente" rows={["otp_verify: true · request_status: 1", "net_balance deduzido pelo app (antes da CF responder)"]} /></MainLane>
          <MainLane><FConn /></MainLane>
          <MainLane><FNode type="cf" title="CF updateBalanceOnOtpVerification dispara" rows={["balance_deducted: true · ledger_created: true", "Ledger criado (status 1) — PATH 1: CF não deduz novamente"]} /></MainLane>
          <MainLane><FConn label="cron a cada 5 min" /></MainLane>

          <MainLane>
            <Box sx={{ border: `2px solid rgba(42,245,152,0.48)`, borderRadius: 3, bgcolor: "rgba(42,245,152,0.07)", p: { xs: 1.75, md: 2 } }}>
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box sx={{ px: 1, py: 0.3, bgcolor: "rgba(42,245,152,0.20)", border: "1px solid rgba(42,245,152,0.50)", borderRadius: 1, fontSize: 10.5, fontWeight: 900, color: C.accent, fontFamily: "monospace" }}>CRON — 5 MIN</Box>
                <Typography sx={{ color: C.white, fontWeight: 900, fontSize: 15 }}>processAutoPayWithdrawal</Typography>
                <Typography sx={{ color: C.muted, fontSize: 13 }}>— avalia todos os requests pendentes</Typography>
              </Stack>
            </Box>
          </MainLane>
          <MainLane><FConn /></MainLane>

          <FGate
            question="fund_rules.auto_pay.enabled === true?"
            noTitle="Fila manual normal"
            noRows={["CF sai sem processar nada", "Admin trata em /admin/withdrawfunds"]}
          />
          <FGate
            question="7 gates de segurança OK? (KYC whitelist · wallet cooldowns · min/max amount · asset · network · janela máx.)"
            noTitle="Bloqueado — auto_pay_blocked_reason gravado"
            noRows={["Exemplos: wallet_too_new, below_min_amount, kyc_not_whitelisted"]}
          />
          <FGate
            question={`accept_delay já passou? (config: ${fmtMin(ap?.accept_delay_minutes)})`}
            noType="wait"
            noTitle="Aguarda próximo tick do cron"
            noRows={["Request permanece em status 1"]}
          />

          <MainLane><FNode type="cf" title="request_status: 1 → 2 (Accepted)" rows={["auto_pay_triggered: true · auto_pay_approved_at: now"]} /></MainLane>
          <MainLane><FConn /></MainLane>

          <FGate
            question={`pay_delay já passou? (config: ${fmtMin(ap?.pay_delay_minutes)})`}
            noType="wait"
            noTitle="Aguarda próximo tick do cron"
            noRows={["Request já em status 2"]}
          />
          <FGate
            question={`daily_cap não estoura com este saque? (cap: ${fmtMoney(ap?.daily_cap)})`}
            noTitle="Manual — daily_cap_exceeded"
            noRows={["Aguardar janela de 24h ou Paid manual"]}
          />
          <FGate
            question="liquidity_wallets[ASSET_NETWORK] preenchida para o par do saque?"
            noTitle="Manual + email admin"
            noRows={["insufficient_liquidity_manual_required", "Email enviado ao admin automaticamente"]}
          />

          <MainLane>
            <Box sx={{ border: `2px solid rgba(42,245,152,0.55)`, borderRadius: 3, bgcolor: "rgba(42,245,152,0.14)", p: { xs: 1.75, md: 2.25 }, boxShadow: "0 0 40px rgba(42,245,152,0.14)" }}>
              <Box sx={{ px: 1, py: 0.3, display: "inline-block", bgcolor: "rgba(42,245,152,0.25)", border: "1px solid rgba(42,245,152,0.55)", borderRadius: 1, fontSize: 10.5, fontWeight: 900, color: C.accent, fontFamily: "monospace", mb: 1 }}>✅ ETAPA 3 — PAID</Box>
              <Typography sx={{ color: C.white, fontWeight: 900, fontSize: 16, mb: 0.75 }}>request_status: 2 → 5 (Paid — Fase 1)</Typography>
              {["auto_pay_phase: 1", "auto_pay_reason: 'auto_paid_phase1_pending_onchain'", "auto_pay_paid_at: serverTimestamp()", "ledger → request_status=5, conclusion_date=now"].map((r) => (
                <Typography key={r} sx={{ color: C.muted, fontSize: 12.5, fontFamily: "monospace", lineHeight: 1.55 }}>{r}</Typography>
              ))}
            </Box>
          </MainLane>
          <MainLane><FConn label="triggers paralelos" /></MainLane>

          <MainLane>
          <Grid container spacing={1.5}>
            {[
              { type: "cf",  tag: "CF", title: "updateBalance SKIP", rows: ["balance_deducted=true → early-return", "Zero double deduction ✅"] },
              { type: "trg", tag: "📧", title: "Email cliente: Withdrawal completed", rows: ["⚠️ Fase 1: dispara antes do pagamento on-chain", "Fase 2: status intermediário vai segurar"] },
              { type: "trg", tag: "📧", title: "Email admin: Auto-Pay ready", rows: ["amount · asset/network · endereço destino", "txId para executar o pagamento"] },
            ].map(({ type, tag, title, rows }) => (
              <Grid item xs={12} md={4} key={title}>
                <Box sx={{ border: `1px solid ${FP[type].b}`, borderLeft: `4px solid ${FP[type].c}`, borderRadius: "4px 10px 10px 4px", bgcolor: FP[type].bg, p: 1.5, height: "100%" }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                    <Box sx={{ px: 0.9, py: 0.15, bgcolor: `${FP[type].c}20`, border: `1px solid ${FP[type].b}`, borderRadius: 1, fontSize: 10, fontWeight: 900, color: FP[type].c, fontFamily: "monospace" }}>{tag}</Box>
                    <Typography sx={{ color: C.white, fontWeight: 800, fontSize: 13, lineHeight: 1.3 }}>{title}</Typography>
                  </Stack>
                  {rows.map((r) => <Typography key={r} sx={{ color: C.muted, fontSize: 12, lineHeight: 1.45 }}>{r}</Typography>)}
                </Box>
              </Grid>
            ))}
          </Grid>
          </MainLane>
          <MainLane><FConn label="ação manual do admin" /></MainLane>

          <MainLane>
            <Box sx={{ border: `2px solid ${FP.man.b}`, borderRadius: 3, bgcolor: FP.man.bg, p: { xs: 1.75, md: 2.25 } }}>
              <Box sx={{ px: 1, py: 0.3, display: "inline-block", bgcolor: `${FP.man.c}20`, border: `1px solid ${FP.man.b}`, borderRadius: 1, fontSize: 10.5, fontWeight: 900, color: FP.man.c, fontFamily: "monospace", mb: 1 }}>ADMIN MANUAL — FASE 1</Box>
              <Typography sx={{ color: C.white, fontWeight: 900, fontSize: 15.5, mb: 0.5 }}>Admin executa o pagamento on-chain (Kraken / Bybit)</Typography>
              {["Abre a exchange e seleciona USDT / Polygon", "Transfere para: selected_wallet_address do request", "Cliente recebe o valor líquido na carteira", "Nenhum clique adicional no Admin Panel"].map((r) => (
                <Typography key={r} sx={{ color: C.muted, fontSize: 13, lineHeight: 1.55 }}>{r}</Typography>
              ))}
              <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5, bgcolor: `${FP.dec.c}10`, border: `1px solid ${FP.dec.b}` }}>
                <Typography sx={{ color: FP.dec.c, fontSize: 12.5, fontWeight: 700 }}>⚡ Fase 2 — Bybit API vai substituir esta etapa automaticamente</Typography>
              </Box>
            </Box>
          </MainLane>

        </FlowCanvas>
      </DialogContent>
    </Dialog>
  );
};

const SectionLabel = ({ children, color = C.accent }) => (
  <Typography sx={{ color, fontSize: 11.5, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", mb: 1.75 }}>
    {children}
  </Typography>
);

const StatChip = ({ label, value, tone = "neutral" }) => {
  const palette = {
    ok:      { color: C.accent,  bg: C.accentDim,  border: C.accentBorder  },
    warn:    { color: C.warn,    bg: C.warnDim,    border: C.warnBorder    },
    danger:  { color: C.danger,  bg: C.dangerDim,  border: C.dangerBorder  },
    info:    { color: C.info,    bg: C.infoDim,    border: C.infoBorder    },
    neutral: { color: C.text,    bg: "rgba(255,255,255,0.04)", border: C.border },
  }[tone];
  return (
    <Box sx={{ px: 2, py: 1.5, borderRadius: 2, bgcolor: palette.bg, border: `1px solid ${palette.border}`, height: "100%" }}>
      <Typography sx={{ color: palette.color, fontSize: 10.5, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ color: C.white, fontSize: 18, fontWeight: 800, lineHeight: 1.25, mt: 0.25 }}>{value}</Typography>
    </Box>
  );
};

const SurfaceCard = ({ title, icon, accent = C.accent, children }) => (
  <Card sx={{ bgcolor: C.surface, border: `1px solid ${C.borderStrong}`, borderRadius: 3, overflow: "hidden", height: "100%", width: "100%" }}>
    <CardContent sx={{ p: 0, height: "100%" }}>
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${C.border}`, bgcolor: "rgba(255,255,255,0.025)", display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${accent}1f`, border: `1px solid ${accent}5a`, flexShrink: 0 }}>
          {React.cloneElement(icon, { sx: { color: accent, fontSize: 18 } })}
        </Box>
        <Typography sx={{ color: C.white, fontSize: 17, fontWeight: 800 }}>{title}</Typography>
      </Box>
      <Box sx={{ p: { xs: 2.5, md: 3 } }}>{children}</Box>
    </CardContent>
  </Card>
);

const FlowStep = ({ num, title, subtitle, color = C.accent, last = false }) => (
  <Box sx={{ display: "flex", gap: 2.5 }}>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: `${color}1f`, border: `2px solid ${color}6e`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Typography sx={{ color, fontWeight: 950, fontSize: 17 }}>{num}</Typography>
      </Box>
      {!last && <Box sx={{ width: 2, minHeight: 44, flexGrow: 1, bgcolor: `${color}28`, my: 0.75 }} />}
    </Box>
    <Box sx={{ pt: 0.75, pb: last ? 0 : 2.25, flex: 1 }}>
      <Typography sx={{ color: C.white, fontSize: 15.5, fontWeight: 800, lineHeight: 1.35 }}>{title}</Typography>
      {subtitle && <Typography sx={{ color: C.muted, fontSize: 14, lineHeight: 1.55, mt: 0.5 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

const StatusBadge = ({ enabled, loading }) => {
  if (loading) return (
    <Chip icon={<CircularProgress size={12} sx={{ color: C.muted }} />} label="Carregando config..." sx={{ color: C.muted, bgcolor: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, fontWeight: 700 }} />
  );
  if (enabled) return (
    <Chip icon={<CheckCircleIcon sx={{ color: `${C.accent} !important` }} />} label="Auto-Pay ATIVO em produção" sx={{ color: C.accent, bgcolor: C.accentDim, border: `1px solid ${C.accentBorder}`, fontWeight: 800 }} />
  );
  return (
    <Chip icon={<WarningAmberIcon sx={{ color: `${C.warn} !important` }} />} label="Auto-Pay DESLIGADO — CF pula todos os saques" sx={{ color: C.warn, bgcolor: C.warnDim, border: `1px solid ${C.warnBorder}`, fontWeight: 800 }} />
  );
};

const NIL = "—";
const fmtMoney = (v) => (v != null && Number(v) > 0 ? `$${Number(v).toLocaleString("en-US")}` : NIL);
const fmtMin = (v) => (v != null ? `${v} min${Number(v) >= 60 ? ` (~${Math.round(Number(v) / 60)}h)` : ""}` : NIL);

const LIQUIDITY_ASSETS = ["USDT", "USDC"];

const buildLiquidityGateRows = (ap) => {
  const assets = (ap?.supported_assets || []).filter((a) => LIQUIDITY_ASSETS.includes(a));
  const networks = ap?.supported_networks || [];
  return assets.flatMap((asset) =>
    networks.map((network) => {
      const key = `${asset}_${network}`;
      return {
        key: `liq_${key}`,
        name: `Liquidity wallet ${asset}/${network}`,
        description: "Carteira operacional Fiddux. Auto-pay para e envia email ao admin se vazia.",
        expected: "Endereço não vazio",
        current: ap?.liquidity_wallets?.[key] || NIL,
        ok: !!ap?.liquidity_wallets?.[key],
        reasonIfFail: "insufficient_liquidity_manual_required",
      };
    })
  );
};

const GATES = (ap) => [
  { key: "enabled",   name: "auto_pay.enabled",                  description: "Master switch. Se false, a CF sai imediatamente sem processar nada.",           expected: "true",                         current: ap?.enabled ? "true" : "false",      ok: !!ap?.enabled,                                                       reasonIfFail: null },
  { key: "kyc",       name: "KYC whitelist",                     description: "Se não vazia, só os UIDs listados recebem auto-pay. Deixe vazio para todos (Fase 2+).", expected: "Vazio OU contém o UID",     current: Array.isArray(ap?.kyc_whitelist_uids) && ap.kyc_whitelist_uids.length > 0 ? `${ap.kyc_whitelist_uids.length} UID(s) — modo restrito` : "Vazio — todos os usuários", ok: true, reasonIfFail: "kyc_not_whitelisted" },
  { key: "wallet_el", name: "Cooldown de criação da wallet",     description: "24h após criação (auto_pay_eligible_from). CF pula wallets dentro do período.", expected: "auto_pay_eligible_from ≤ now",  current: "Por wallet (Wallet Management)",     ok: true,                                                                reasonIfFail: "wallet_too_new" },
  { key: "wallet_ed", name: "Cooldown de edição da wallet",      description: "24h após qualquer edição (edit_suspended_until). CF aguarda.",                   expected: "edit_suspended_until ≤ now",   current: "Por wallet (Wallet Management)",     ok: true,                                                                reasonIfFail: "wallet_recently_edited" },
  { key: "min",       name: "Min amount por saque",              description: "Saques abaixo desse valor ficam na fila manual. Default $100.",                  expected: ">= 1",                         current: fmtMoney(ap?.min_amount),            ok: Number(ap?.min_amount || 0) > 0,                                     reasonIfFail: "below_min_amount" },
  { key: "max",       name: "Max amount por saque",              description: "Saques acima ficam fora do auto-pay (revisão manual).",                          expected: ">= min_amount",                current: fmtMoney(ap?.max_amount),            ok: Number(ap?.max_amount || 0) > Number(ap?.min_amount || 0),           reasonIfFail: null },
  { key: "assets",    name: "Assets suportados",                 description: "Só saques nesses assets são processados automaticamente.",                       expected: "USDT e/ou USDC",               current: (ap?.supported_assets || []).join(", ") || NIL, ok: (ap?.supported_assets || []).length > 0,                           reasonIfFail: "unsupported_asset" },
  { key: "networks",  name: "Networks suportadas",               description: "Só saques nessas networks são processados automaticamente.",                     expected: "POLYGON, BSC, …",              current: (ap?.supported_networks || []).join(", ") || NIL, ok: (ap?.supported_networks || []).length > 0,                         reasonIfFail: "unsupported_network" },
  { key: "window",    name: "Janela máxima (horas)",             description: "Requests mais antigos que este limite são ignorados.",                           expected: "24h (default)",                current: ap?.max_window_hours != null ? `${ap.max_window_hours}h` : NIL, ok: Number(ap?.max_window_hours || 0) > 0,                             reasonIfFail: null },
  { key: "dailycap",  name: "Daily cap (USD)",                   description: "Soma dos saques auto-pagos nas últimas 24h. Novo saque além do cap é bloqueado.", expected: ">= max_amount",               current: fmtMoney(ap?.daily_cap),             ok: Number(ap?.daily_cap || 0) >= Number(ap?.max_amount || 0),           reasonIfFail: "daily_cap_exceeded" },
  ...buildLiquidityGateRows(ap),
];

const MANUAL_EXITS = [
  ["enabled === false",                   "Status 1 (sem flag)",                     "Admin processa pela fila normal de Withdrawals"],
  ["kyc_not_whitelisted",                 "Status 1 + blocked_reason",               "Admin revisa manualmente; adicionar UID ou abrir whitelist"],
  ["wallet_too_new (cooldown 24h)",       "Status 1 + wallet_too_new",               "Aguardar cooldown OU Paid manual"],
  ["wallet_recently_edited (cooldown)",   "Status 1 + wallet_recently_edited",       "Aguardar 24h pós-edição OU Paid manual"],
  ["below_min_amount",                    "Status 1 + below_min_amount",             "Admin processa manualmente"],
  ["amount > max_amount",                 "Status 1 (sem flag)",                     "Revisão manual de alto valor (alerta de email separado)"],
  ["unsupported_asset / unsupported_network", "Status 1 + blocked_reason",           "Admin processa manualmente ou habilitar asset/rede em Admin Settings"],
  ["Fora da janela (> max_window_hours)", "Status 1 (sem flag)",                     "Admin processa manualmente"],
  ["daily_cap_exceeded",                  "Status 2 + daily_cap_exceeded",           "Aguardar renovar janela 24h OU Paid manual"],
  ["insufficient_liquidity",             "Status 2 + flag + email admin",            "Fundiar a liquidity wallet OU Paid manual"],
];

const TRIGGERS = [
  { name: "updateBalanceOnOtpVerification", where: "functions/index.js", text: "Vê balance_deducted=true (setado no OTP) → early-return. Zero double deduction — proteção do WITHDRAWAL_DOUBLE_DEDUCTION_FIX.", tone: "ok" },
  { name: "Email cliente: \"Withdrawal completed\"", where: "functions/index.js — trigger on status 5", text: "Enviado automaticamente. ⚠️ Fase 1: dispara ANTES do pagamento on-chain. Fase 2 introduzirá status intermediário auto_pay_payout_pending.", tone: "warn" },
  { name: "Email admin: \"Auto-Pay ready: execute on-chain\"", where: "processAutoPayWithdrawal", text: "Inclui: nome do cliente, amount, asset/network, endereço destino, txId.", tone: "ok" },
  { name: "Ledger atualizado para status 5", where: "processAutoPayWithdrawal", text: "Espelha o comportamento manual do WithdrowFunds.jsx: seta request_status=5 + conclusion_date no ledger existente.", tone: "ok" },
];

const TIMELINE = [
  ["T+0",      "0 → 1",        "App após OTP — net_balance já deduzido pelo app"],
  ["T+0",      "ledger criado","CF updateBalanceOnOtpVerification (balance_deducted: true)"],
  ["T+~5min",  "1 → 2",        "CF processAutoPayWithdrawal (após accept_delay + próximo cron)"],
  ["T+~10min", "2 → 5",        "CF processAutoPayWithdrawal (após pay_delay) + emails cliente + admin"],
  ["T+~10min", "Admin age",    "Admin executa transferência on-chain da liquidity wallet → carteira cliente"],
  ["Após pag.", "Cliente recebe", "USDT na carteira; sistema já em status 5"],
];

const NOT_AUTO = [
  "Assinatura e envio da transação on-chain (admin usa Kraken/Bybit manualmente).",
  "Confirmação blockchain (tx hash ainda não é capturado no request document).",
  "Débito da carteira de liquidez Fiddux (admin move os fundos).",
  "Reconciliação com extrato da exchange.",
  "Status intermediário auto_pay_payout_pending (Fase 2) — segura o email do cliente até confirmação.",
];

const AutoPayFlow = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(null);
  const [flowOpen, setFlowOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getFundRules();
      setRules(data || {});
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load fund_rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const ap = useMemo(() => rules?.auto_pay || null, [rules]);
  const gates = useMemo(() => GATES(ap || {}), [ap]);
  const enabled = !!ap?.enabled;
  const whitelistCount = (ap?.kyc_whitelist_uids || []).length;
  const blockingCount = gates.filter((g) => !g.ok).length;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg, color: C.white, pb: 6 }}>
      <FlowchartDialog open={flowOpen} onClose={() => setFlowOpen(false)} ap={ap || {}} />

      {/* ── Header ── */}
      <Box
        sx={{
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          borderBottom: `1px solid ${C.borderStrong}`,
          background: "radial-gradient(circle at 14% 18%, rgba(42,245,152,0.15) 0%, transparent 34%), radial-gradient(circle at 86% 4%, rgba(66,165,245,0.13) 0%, transparent 28%), linear-gradient(135deg, #07111a 0%, #070a12 75%)",
        }}
      >
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md>
            <Stack direction="row" spacing={2.25} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ width: 58, height: 58, borderRadius: 3, bgcolor: C.accentDim, border: `1px solid ${C.accentBorder}`, boxShadow: "0 0 36px rgba(42,245,152,0.14)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <BoltIcon sx={{ color: C.accent, fontSize: 30 }} />
              </Box>
              <Box>
                <Typography sx={{ color: C.accent, fontSize: 11.5, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase" }}>Auto-Pay — Phase 1</Typography>
                <Typography sx={{ color: C.white, fontSize: { xs: 26, md: 40 }, fontWeight: 950, lineHeight: 1.05 }}>How Auto-Pay works right now</Typography>
              </Box>
            </Stack>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: { xs: 15, md: 17 }, lineHeight: 1.6, maxWidth: 760 }}>
              Cloud Function leva saques de <strong>status 1 → 2 → 5 (Paid)</strong> automaticamente quando todos os gates passam. A transferência on-chain ainda é executada manualmente até a Fase 2 (Bybit API). Esta página lê <code>fund_rules/active.auto_pay</code> ao vivo do Firestore.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <StatusBadge enabled={enabled} loading={loading} />
              {error && <Chip label={`Erro: ${error}`} sx={{ ml: 1.5, color: C.danger, bgcolor: C.dangerDim, border: `1px solid ${C.dangerBorder}`, fontWeight: 700 }} />}
            </Box>
          </Grid>
          <Grid item xs={12} md="auto">
            <Stack spacing={1.25} sx={{ minWidth: 220 }}>
              <Button
                startIcon={<AccountTreeIcon />}
                onClick={() => setFlowOpen(true)}
                variant="contained"
                sx={{ bgcolor: C.accent, color: "#000", fontWeight: 950, fontSize: 14.5, py: 1.25, borderRadius: 2, boxShadow: "0 0 28px rgba(42,245,152,0.20)", "&:hover": { bgcolor: "#1de585" } }}
              >
                Fluxograma
              </Button>
              <Button
                startIcon={<RestartAltIcon />}
                onClick={load}
                disabled={loading}
                sx={{ color: C.white, border: `1px solid ${C.borderStrong}`, borderRadius: 2, fontSize: 14, bgcolor: "rgba(255,255,255,0.04)", "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.07)" } }}
              >
                Refresh config
              </Button>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin/security-architecture")}
                sx={{ color: C.white, border: `1px solid ${C.borderStrong}`, borderRadius: 2, fontSize: 14, bgcolor: "rgba(255,255,255,0.04)", "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.07)" } }}
              >
                Security Architecture
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: { xs: 3, md: 5 }, py: 4 }}>

        {/* Stats chips — 4 por linha em md+ */}
        <SectionLabel>Config ao vivo (fund_rules/active.auto_pay)</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: "Status",        value: enabled ? "Enabled" : "Disabled",                          tone: enabled ? "ok" : "warn"                          },
            { label: "Min amount",    value: fmtMoney(ap?.min_amount),                                  tone: (ap?.min_amount || 0) > 0 ? "ok" : "warn"        },
            { label: "Max amount",    value: fmtMoney(ap?.max_amount),                                  tone: (ap?.max_amount || 0) > 0 ? "ok" : "warn"        },
            { label: "Daily cap",     value: fmtMoney(ap?.daily_cap),                                   tone: (ap?.daily_cap || 0) > 0 ? "ok" : "neutral"      },
            { label: "Accept delay",  value: fmtMin(ap?.accept_delay_minutes),                          tone: "info"                                            },
            { label: "Pay delay",     value: fmtMin(ap?.pay_delay_minutes),                             tone: "info"                                            },
            { label: "KYC whitelist", value: whitelistCount > 0 ? `${whitelistCount} UID(s)` : "All users", tone: whitelistCount > 0 ? "warn" : "neutral"      },
            { label: "Gates bloq.",   value: loading ? "..." : `${blockingCount}`,                      tone: blockingCount === 0 ? "ok" : "warn"               },
          ].map(({ label, value, tone }) => (
            <Grid item xs={6} sm={3} key={label}>
              <StatChip label={label} value={value} tone={tone} />
            </Grid>
          ))}
        </Grid>

        {/* Cards 2×3 com altura igual por linha */}
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <SurfaceCard title="End-to-end flow" icon={<RouteIcon />} accent={C.accent}>
              <FlowStep num="0" title="Client requests withdrawal (Flutter app)" subtitle="Status 0 (Processing) — OTP pending." color={C.info} />
              <FlowStep num="1" title="OTP verified by client" subtitle="App: status 1, otp_verify=true, net_balance deducted. CF creates ledger + balance_deducted=true." color={C.info} />
              <FlowStep num="2" title="processAutoPayWithdrawal cron (every 5 min)" subtitle="Checks enabled + evaluates all security gates." color={C.accent} />
              <FlowStep num="3" title="Accept window passes → status 2 (Accepted)" subtitle={`After accept_delay (${fmtMin(ap?.accept_delay_minutes)}). Sets auto_pay_triggered=true.`} color={C.accent} />
              <FlowStep num="4" title="Pay window passes → status 5 (Paid — Phase 1)" subtitle={`After pay_delay (${fmtMin(ap?.pay_delay_minutes)}). Updates ledger.`} color={C.accent} />
              <FlowStep num="5" title="Parallel triggers fire" subtitle="Balance trigger SKIPS. Client email + admin email sent." color={C.warn} />
              <FlowStep num="6" title="Admin executes on-chain transfer" subtitle="Manual step — Phase 1. Kraken/Bybit → selected_wallet_address." color={C.warn} last />
            </SurfaceCard>
          </Grid>
          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <SurfaceCard title="Gates (todos precisam passar)" icon={<RuleIcon />} accent={C.info}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border }}>Gate</TableCell>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border }}>Valor atual</TableCell>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border, width: 52 }}>OK</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gates.map((g) => (
                      <TableRow key={g.key} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.025)" } }}>
                        <TableCell sx={{ borderColor: C.border, py: 1.25 }}>
                          <Typography sx={{ color: C.white, fontSize: 13.5, fontWeight: 700 }}>{g.name}</Typography>
                          <Typography sx={{ color: C.muted, fontSize: 12, lineHeight: 1.4, mt: 0.2 }}>{g.description}</Typography>
                          {g.reasonIfFail && <Typography sx={{ color: C.faint, fontSize: 11, fontFamily: "monospace", mt: 0.35 }}>→ {g.reasonIfFail}</Typography>}
                        </TableCell>
                        <TableCell sx={{ borderColor: C.border, py: 1.25 }}>
                          <Typography sx={{ color: C.text, fontSize: 12.5, fontFamily: "monospace", wordBreak: "break-all" }}>{g.current}</Typography>
                          <Typography sx={{ color: C.faint, fontSize: 11, mt: 0.2 }}>Esperado: {g.expected}</Typography>
                        </TableCell>
                        <TableCell sx={{ borderColor: C.border, py: 1.25 }}>
                          {g.ok ? <CheckCircleIcon sx={{ color: C.accent, fontSize: 20 }} /> : <RadioButtonUncheckedIcon sx={{ color: C.warn, fontSize: 20 }} />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SurfaceCard>
          </Grid>

          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <SurfaceCard title="Quando vira manual" icon={<WarningAmberIcon />} accent={C.warn}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border }}>Condição</TableCell>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border }}>Estado</TableCell>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border }}>Ação do admin</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MANUAL_EXITS.map(([trigger, state, action]) => (
                      <TableRow key={trigger} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.025)" } }}>
                        <TableCell sx={{ borderColor: C.border, color: C.white, fontSize: 13, fontWeight: 700, py: 1.25, fontFamily: "monospace" }}>{trigger}</TableCell>
                        <TableCell sx={{ borderColor: C.border, color: C.text, fontSize: 12.5, py: 1.25 }}>{state}</TableCell>
                        <TableCell sx={{ borderColor: C.border, color: C.muted, fontSize: 12.5, py: 1.25 }}>{action}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SurfaceCard>
          </Grid>
          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <SurfaceCard title="Triggers quando status = 5" icon={<MailOutlineIcon />} accent={C.info}>
              <Stack spacing={1.5}>
                {TRIGGERS.map((t) => {
                  const tone = t.tone === "ok"
                    ? { color: C.accent, bg: C.accentDim, border: C.accentBorder, icon: <CheckCircleIcon sx={{ color: C.accent, flexShrink: 0 }} /> }
                    : { color: C.warn, bg: C.warnDim, border: C.warnBorder, icon: <WarningAmberIcon sx={{ color: C.warn, flexShrink: 0 }} /> };
                  return (
                    <Box key={t.name} sx={{ p: 1.75, borderRadius: 2, bgcolor: tone.bg, border: `1px solid ${tone.border}` }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {tone.icon}
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ color: tone.color, fontSize: 13.5, fontWeight: 800 }}>{t.name}</Typography>
                          <Typography sx={{ color: C.faint, fontSize: 11, fontFamily: "monospace", mt: 0.2 }}>{t.where}</Typography>
                          <Typography sx={{ color: C.text, fontSize: 13, lineHeight: 1.55, mt: 0.6 }}>{t.text}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </SurfaceCard>
          </Grid>

          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <SurfaceCard title="Timeline do teste (fundador)" icon={<HourglassEmptyIcon />} accent={C.accent}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border, width: 96 }}>Tempo</TableCell>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border }}>Status</TableCell>
                      <TableCell sx={{ color: C.muted, fontWeight: 800, borderColor: C.border }}>Evento</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {TIMELINE.map(([t, s, who]) => (
                      <TableRow key={`${t}-${s}`}>
                        <TableCell sx={{ borderColor: C.border, color: C.accent, fontSize: 12.5, fontWeight: 800, py: 1.25, fontFamily: "monospace" }}>{t}</TableCell>
                        <TableCell sx={{ borderColor: C.border, color: C.white, fontSize: 13, py: 1.25, fontWeight: 700 }}>{s}</TableCell>
                        <TableCell sx={{ borderColor: C.border, color: C.text, fontSize: 12.5, py: 1.25 }}>{who}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ borderColor: C.border, my: 2 }} />
              <Typography sx={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
                <strong style={{ color: C.warn }}>Atenção Fase 1:</strong> o email "Withdrawal completed" chega ao cliente antes da transferência on-chain. Mantenha o KYC whitelist preenchido durante o teste.
              </Typography>
            </SurfaceCard>
          </Grid>
          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <SurfaceCard title="Ainda não automatizado (Fase 1)" icon={<WarningAmberIcon />} accent={C.danger}>
              <Stack spacing={1.25}>
                {NOT_AUTO.map((line) => (
                  <Box key={line} sx={{ display: "flex", gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: C.dangerDim, border: `1px solid ${C.dangerBorder}` }}>
                    <WarningAmberIcon sx={{ color: C.danger, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                    <Typography sx={{ color: C.text, fontSize: 13.5, lineHeight: 1.55 }}>{line}</Typography>
                  </Box>
                ))}
              </Stack>
            </SurfaceCard>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <SectionLabel>Atalhos</SectionLabel>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button fullWidth variant="outlined" endIcon={<ArrowForwardIcon />} startIcon={<SettingsIcon />} onClick={() => navigate("/admin/admin-settings")} sx={{ color: C.white, borderColor: C.borderStrong, fontSize: 14, py: 1.35, "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.06)" } }}>Configure Auto-Pay</Button>
            <Button fullWidth variant="outlined" endIcon={<ArrowForwardIcon />} startIcon={<AccountBalanceWalletIcon />} onClick={() => navigate("/admin/wallet-management")} sx={{ color: C.white, borderColor: C.borderStrong, fontSize: 14, py: 1.35, "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.06)" } }}>Wallet Management</Button>
            <Button fullWidth variant="outlined" endIcon={<ArrowForwardIcon />} startIcon={<RouteIcon />} onClick={() => navigate("/admin/withdrawfunds")} sx={{ color: C.white, borderColor: C.borderStrong, fontSize: 14, py: 1.35, "&:hover": { borderColor: C.accentBorder, bgcolor: "rgba(42,245,152,0.06)" } }}>Withdrawals queue</Button>
          </Stack>
        </Box>

      </Box>
    </Box>
  );
};

export default AutoPayFlow;
